import {
  Center,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton
} from '@mantine/core'
import { motion } from 'framer-motion'

import { colors } from 'src/theme/colors'
import { getIcon } from 'src/utils'

import {
  accentVariants,
  arrowVariants,
  liftVariants,
  SHADOW_REST
} from '../cardMotion'

export type ResourceCardProps = {
  title: string
  description?: string
  iconUrl?: string
  onClick?: () => void
}

const IconTile = ({ title, iconUrl }: { title: string; iconUrl?: string }) => (
  <Center w={46} h={46} mb={14} bdrs={12} bg="var(--mantine-color-gray-2)">
    {getIcon({ title, iconUrl, color: colors.lightGray, size: 22 })}
  </Center>
)

export const ResourceCard = ({
  title,
  description,
  iconUrl,
  onClick
}: ResourceCardProps) => {
  if (!onClick) {
    return (
      <Paper
        withBorder
        radius={16}
        p={22}
        h="100%"
        opacity={0.72}
        style={{ boxShadow: SHADOW_REST }}
      >
        <Stack gap={0} h="100%">
          <IconTile title={title} iconUrl={iconUrl} />
          <Text fw={700} fz={16} lh={1.3} mb={6}>
            {title}
          </Text>
          {description && (
            <Text fz={13.5} c={colors.lightGray} lh={1.55}>
              {description}
            </Text>
          )}
        </Stack>
      </Paper>
    )
  }

  return (
    <UnstyledButton w="100%" h="100%" onClick={onClick}>
      <motion.div
        initial="rest"
        animate="rest"
        whileHover="hover"
        variants={liftVariants}
        style={{ height: '100%', borderRadius: 16 }}
      >
        <Paper
          withBorder
          radius={16}
          p={22}
          h="100%"
          pos="relative"
          className="overflow-hidden"
        >
          <motion.div
            aria-hidden
            variants={accentVariants}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: colors.success,
              transformOrigin: 'top'
            }}
          />
          <Stack gap={0} h="100%">
            <IconTile title={title} iconUrl={iconUrl} />
            <Text fw={700} fz={16} lh={1.3} mb={6}>
              {title}
            </Text>
            {description && (
              <Text fz={13.5} c={colors.lightGray} lh={1.55} flex={1}>
                {description}
              </Text>
            )}
            <Group gap={6} mt={14} wrap="nowrap">
              <Text fz={13} fw={700} c="var(--mantine-color-green-8)">
                Open
              </Text>
              <motion.span
                variants={arrowVariants}
                style={{
                  display: 'inline-block',
                  color: 'var(--mantine-color-green-8)'
                }}
              >
                →
              </motion.span>
            </Group>
          </Stack>
        </Paper>
      </motion.div>
    </UnstyledButton>
  )
}
